import React from 'react';

import { css } from '@emotion/react';
import { CssVars, InfoBanner, Logo, Type } from '@robinhood/design';
import { useRouter } from 'next/router';
import QRCode from 'react-qr-code';

import { CARD_APP_STORE_URL, CARD_PLAY_STORE_URL } from 'src/utils/constants';
import { PlatformType, detectPlatform, isAppleDevice } from 'src/utils/detectPlatform';

type SlugType = string[] | undefined;

interface RouterQueryIface {
  deeplinkSlug: SlugType;
  [key: string]: string | string[] | undefined;
}

const APP_SCHEME = 'robinhood-cc://';
const genDeepLink = (deeplinkSlug: SlugType, params: URLSearchParams): string => {
  const slugPath = (deeplinkSlug ?? []).join('/');
  const qs = params.toString();
  const queryString = qs.length > 0 ? `?${qs}` : '';
  return `${APP_SCHEME}${slugPath}${queryString}`;
};

/**
 * If a deeplinkSlug is provided, the page will render twice: once to hydrate the initial HTML,
 * and once to populate the dynamic router params. We want to generate the mobile deep
 * link with the most recent deeplinkSlug.
 *
 * https://nextjs.org/docs/advanced-features/automatic-static-optimization#how-it-works
 */
const PRERENDER_DURATION = 200;

const AppDeeplinkPage = () => {
  const router = useRouter();
  const { deeplinkSlug, ...params } = router.query as RouterQueryIface;
  const [platform, setPlatform] = React.useState<PlatformType | undefined>();
  const [isApple, setIsApple] = React.useState<boolean>(false);
  const [deepLink, setDeepLink] = React.useState<string | undefined>();
  const [qrCode, setQrCode] = React.useState<string | undefined>();
  const mostRecentSlugRef = React.useRef<SlugType | undefined>(undefined);

  const downloadAppUrl = isApple ? CARD_APP_STORE_URL : CARD_PLAY_STORE_URL;

  const onPressClickHere = React.useCallback(() => {
    const now = Date.now();

    setTimeout(() => {
      const elapsed = Date.now() - now;
      if (elapsed < 1500) {
        // App didn't open, redirect to the download page
        window.location.href = downloadAppUrl;
      }
    }, 1000);

    // Attempt to open the deeplink
    window.location.href = deepLink ?? '';
  }, [deepLink, downloadAppUrl]);

  React.useEffect(() => {
    setPlatform(detectPlatform());
    setIsApple(isAppleDevice());
  }, []);

  React.useEffect(() => {
    const p = detectPlatform();
    setPlatform(p);
  }, []);

  React.useEffect(() => {
    if (deepLink != null && platform != 'Desktop') {
      window.location.href = deepLink;
    }
  }, [deepLink, platform]);

  React.useEffect(() => {
    mostRecentSlugRef.current = deeplinkSlug;
  }, [deeplinkSlug]);

  React.useEffect(() => {
    // NOTE: QR fix for Help Center deep-links because query params URL is too long for QR specs
    setQrCode(
      deeplinkSlug?.includes('help-center') ? `${window.location.origin}/app/help-center/` : window.location.href,
    );
  }, [deeplinkSlug]);

  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      const searchParams = new URLSearchParams(params as Record<string, string>);
      const link = genDeepLink(mostRecentSlugRef.current, searchParams);
      setDeepLink(link);
    }, PRERENDER_DURATION);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [platform, params]);

  return (
    <div css={page}>
      <Logo includeWordmark overrideRootStyles={header} />
      <div css={root}>
        <Type.Display size='M' overrideRootStyles={centerText}>
          Open the Robinhood Credit Card app
        </Type.Display>
        <Type.Text size='M' overrideRootStyles={centerText}>
          Please open this page on your phone.
        </Type.Text>
        {qrCode != null && <QRCode value={qrCode} size={200} />}
        {deepLink != null && (
          <InfoBanner
            callToAction='Click here'
            iconType='Question24'
            onCallToActionClick={onPressClickHere}
            text='Already on your phone?'
            overrideRootStyles={fullWidth}
          />
        )}
      </div>
    </div>
  );
};

const page = css`
  position: fixed;
  height: 100vh;
  width: 100vw;
  display: flex;
  flex-direction: column;
`;

const header = css`
  padding: 8px 0 8px 32px;
  border-bottom: 1px solid ${CssVars.neutral.fg1};
  width: 100%;
`;

const root = css`
  align-self: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 600px;
  width: '100%';
  margin-top: 60px;
  gap: 24px;
  padding: 0px 32px;
`;

const fullWidth = css`
  width: 100%;
`;

const centerText = css`
  text-align: center;
`;

export default AppDeeplinkPage;
